import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Users, 
  Building,
  MessageCircle,
  ExternalLink,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Contact {
  id: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  address?: string;
  workingHours?: string;
  category: 'office' | 'support' | 'external' | 'personal';
  notes?: string;
  website?: string;
  createdAt: string;
}

const Contacts = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    address: "",
    workingHours: "",
    category: "office" as Contact['category'],
    notes: "",
    website: ""
  });

  // Carregar contatos do localStorage
  useEffect(() => {
    const savedContacts = localStorage.getItem('contacts');
    if (savedContacts) {
      setContacts(JSON.parse(savedContacts));
    } else {
      // Contatos padrão
      const defaultContacts: Contact[] = [
        {
          id: "1",
          name: "Allana Sthéfany",
          role: "CEO & Advogada",
          phone: "(11) 99999-9999",
          email: "allana@jurishub.com",
          address: "Rua das Flores, 123 - Centro, São Paulo/SP",
          workingHours: "Segunda a Sexta: 8h às 18h",
          category: "office",
          createdAt: new Date().toISOString()
        },
        {
          id: "2",
          name: "Suporte Técnico",
          role: "Suporte",
          phone: "(11) 88888-8888",
          email: "suporte@jurishub.com",
          workingHours: "Segunda a Sexta: 9h às 17h",
          category: "support",
          createdAt: new Date().toISOString()
        },
        {
          id: "3",
          name: "OAB/SP",
          role: "Ordem dos Advogados",
          phone: "(11) 3333-3333",
          email: "contato@oabsp.org.br",
          website: "https://www.oabsp.org.br",
          category: "external",
          createdAt: new Date().toISOString()
        }
      ];
      setContacts(defaultContacts);
      localStorage.setItem('contacts', JSON.stringify(defaultContacts));
    }
  }, []);

  // Filtrar contatos
  useEffect(() => {
    let filtered = contacts;

    if (searchTerm) {
      filtered = filtered.filter(contact =>
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone.includes(searchTerm) ||
        contact.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "all") {
      filtered = filtered.filter(contact => contact.category === selectedCategory);
    }

    setFilteredContacts(filtered);
  }, [contacts, searchTerm, selectedCategory]);

  const saveContacts = (newContacts: Contact[]) => {
    setContacts(newContacts);
    localStorage.setItem('contacts', JSON.stringify(newContacts));
  };

  const handleAddContact = () => {
    if (!formData.name || !formData.phone) {
      toast({
        title: "Erro",
        description: "Nome e telefone são obrigatórios",
        variant: "destructive"
      });
      return;
    }

    const newContact: Contact = {
      id: Date.now().toString(),
      ...formData,
      createdAt: new Date().toISOString()
    };

    const updatedContacts = [...contacts, newContact];
    saveContacts(updatedContacts);
    
    toast({
      title: "Sucesso!",
      description: "Contato adicionado com sucesso"
    });

    resetForm();
    setIsAddDialogOpen(false);
  };

  const handleEditContact = () => {
    if (!editingContact) return;

    if (!formData.name || !formData.phone) {
      toast({
        title: "Erro",
        description: "Nome e telefone são obrigatórios",
        variant: "destructive"
      });
      return;
    }

    const updatedContacts = contacts.map(contact =>
      contact.id === editingContact.id ? { ...contact, ...formData } : contact
    );

    saveContacts(updatedContacts);
    
    toast({
      title: "Sucesso!",
      description: "Contato atualizado com sucesso"
    });

    resetForm();
    setEditingContact(null);
    setIsAddDialogOpen(false);
  };

  const handleDeleteContact = () => {
    if (!contactToDelete) return;

    const updatedContacts = contacts.filter(contact => contact.id !== contactToDelete.id);
    saveContacts(updatedContacts);
    
    toast({
      title: "Sucesso!",
      description: "Contato removido com sucesso"
    });

    setContactToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      role: "",
      phone: "",
      email: "",
      address: "",
      workingHours: "",
      category: "office",
      notes: "",
      website: ""
    });
  };

  const openEditDialog = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      role: contact.role,
      phone: contact.phone,
      email: contact.email,
      address: contact.address || "",
      workingHours: contact.workingHours || "",
      category: contact.category,
      notes: contact.notes || "",
      website: contact.website || ""
    });
    setIsAddDialogOpen(true);
  };

  const openDeleteDialog = (contact: Contact) => {
    setContactToDelete(contact);
    setIsDeleteDialogOpen(true);
  };

  const getCategoryLabel = (category: Contact['category']) => {
    switch (category) {
      case 'office': return 'Escritório';
      case 'support': return 'Suporte';
      case 'external': return 'Externo';
      case 'personal': return 'Pessoal';
      default: return 'Outro';
    }
  };

  const getCategoryColor = (category: Contact['category']) => {
    switch (category) {
      case 'office': return 'bg-primary/10 text-primary';
      case 'support': return 'bg-blue-100 text-blue-600';
      case 'external': return 'bg-green-100 text-green-600';
      case 'personal': return 'bg-purple-100 text-purple-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleCall = (phone: string) => {
    window.open(`tel:${phone}`, '_blank');
  };

  const handleEmail = (email: string) => {
    window.open(`mailto:${email}`, '_blank');
  };

  const handleWebsite = (website: string) => {
    window.open(website, '_blank');
  };

  const handleWhatsApp = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    const full = digits.length <= 11 ? `55${digits}` : digits;
    window.open(`https://wa.me/${full}`, '_blank');
  };

  const initials = (name: string) =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();

  const categoryTone: Record<Contact['category'], string> = {
    office: 'bg-[#c4ab83]/15 text-[#7a6440]',
    support: 'bg-blue-50 text-blue-700',
    external: 'bg-emerald-50 text-emerald-700',
    personal: 'bg-violet-50 text-violet-700',
  };

  const categories: { value: string; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'office', label: 'Escritório' },
    { value: 'support', label: 'Suporte' },
    { value: 'external', label: 'Externo' },
    { value: 'personal', label: 'Pessoal' },
  ];
  const countFor = (value: string) => value === 'all' ? contacts.length : contacts.filter(c => c.category === value).length;

  const fieldLabel = 'block text-sm font-medium text-slate-700';
  const fieldInput = 'w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30';

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-4 md:p-6">
      {/* Cabeçalho */}
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Agenda</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Contatos</h1>
          <p className="mt-1 text-sm text-slate-500">
            {contacts.length} {contacts.length === 1 ? 'contato salvo' : 'contatos salvos'} para falar rápido.
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingContact(null);
            setIsAddDialogOpen(true);
          }}
          className="h-10 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800"
        >
          <Plus className="mr-2 h-4 w-4 text-[#e8d5a8]" />
          Novo contato
        </Button>
      </header>

      {/* Busca e categorias */}
      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200/80 bg-white p-2 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar por nome, cargo, telefone ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-10 rounded-xl border-0 bg-slate-50 pl-9 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
          {categories.map(cat => {
            const active = selectedCategory === cat.value;
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => setSelectedCategory(cat.value)}
                className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors ${active ? 'bg-white font-semibold text-[#7a6440] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              >
                {cat.label}
                <span className={`tabular-nums text-[11px] ${active ? 'text-[#9a7f52]' : 'text-slate-400'}`}>{countFor(cat.value)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista de contatos */}
      {filteredContacts.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredContacts.map((contact) => (
            <div
              key={contact.id}
              className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#c4ab83]/50 hover:shadow-[0_12px_30px_-18px_rgba(122,100,64,0.45)]"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#e8d5a8] to-[#c4ab83] text-sm font-semibold text-white">
                  {initials(contact.name) || <Users className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold tracking-tight text-slate-900">{contact.name}</p>
                  <p className="truncate text-sm text-slate-500">{contact.role || 'Sem cargo'}</p>
                </div>
                <div className="flex shrink-0 gap-0.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => openEditDialog(contact)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="Editar"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openDeleteDialog(contact)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <span className={`mt-3 w-fit rounded-full px-2 py-0.5 text-[11px] font-medium ${categoryTone[contact.category] || 'bg-slate-100 text-slate-600'}`}>
                {getCategoryLabel(contact.category)}
              </span>

              <div className="mt-4 flex-1 space-y-2.5 text-sm">
                <div className="flex items-center gap-3 text-slate-700">
                  <Phone className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                  <span className="tabular-nums">{contact.phone}</span>
                </div>
                {contact.email && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <Mail className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                    <span className="truncate">{contact.email}</span>
                  </div>
                )}
                {contact.address && (
                  <div className="flex items-start gap-3 text-slate-600">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#c4ab83]" />
                    <span>{contact.address}</span>
                  </div>
                )}
                {contact.workingHours && (
                  <div className="flex items-center gap-3 text-slate-600">
                    <Clock className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                    <span>{contact.workingHours}</span>
                  </div>
                )}
                {contact.website && (
                  <div className="flex items-center gap-3">
                    <ExternalLink className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                    <a
                      href={contact.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-[#9a7f52] hover:underline"
                    >
                      {contact.website.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </div>
                )}
                {contact.notes && (
                  <p className="rounded-xl bg-[#faf7f2] px-3 py-2 text-[13px] text-slate-600">{contact.notes}</p>
                )}
              </div>

              <div className="mt-5 grid grid-cols-3 gap-1 border-t border-slate-100 pt-3">
                <button
                  type="button"
                  onClick={() => handleCall(contact.phone)}
                  className="flex items-center justify-center gap-1.5 rounded-xl py-2 text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-900 hover:text-white"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Ligar
                </button>
                <button
                  type="button"
                  onClick={() => handleWhatsApp(contact.phone)}
                  className="flex items-center justify-center gap-1.5 rounded-xl py-2 text-[13px] font-medium text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => contact.email && handleEmail(contact.email)}
                  disabled={!contact.email}
                  className="flex items-center justify-center gap-1.5 rounded-xl py-2 text-[13px] font-medium text-slate-700 transition-colors hover:bg-[#c4ab83]/15 hover:text-[#7a6440] disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#faf7f2]">
            <Users className="h-5 w-5 text-[#c4ab83]" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-900">Nenhum contato encontrado</h3>
          <p className="mt-1 text-sm text-slate-500">
            {searchTerm || selectedCategory !== "all"
              ? "Tente outra busca ou categoria."
              : "Adicione seu primeiro contato no botão acima."}
          </p>
        </div>
      )}

      {/* Novo / editar contato */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
          <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            {/* Monograma AS como marca d'água */}
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative flex items-center gap-4 text-left">
              <img
                src="/imgs/logos allana/letra_A_logo.png"
                alt="Allana Sthéfany"
                className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
              />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Contatos</p>
                <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                  {editingContact ? "Editar contato" : "Novo contato"}
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-500">
                  Só nome e telefone são obrigatórios.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="name" className={fieldLabel}>Nome *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={fieldInput} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className={fieldLabel}>Cargo</Label>
              <Input id="role" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className={fieldInput} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category" className={fieldLabel}>Categoria</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value as Contact['category'] })}>
                <SelectTrigger className={fieldInput}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">Escritório</SelectItem>
                  <SelectItem value="support">Suporte</SelectItem>
                  <SelectItem value="external">Externo</SelectItem>
                  <SelectItem value="personal">Pessoal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className={fieldLabel}>Telefone *</Label>
              <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(81) 99999-9999" className={fieldInput} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className={fieldLabel}>Email</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={fieldInput} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address" className={fieldLabel}>Endereço</Label>
              <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={fieldInput} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workingHours" className={fieldLabel}>Horário</Label>
              <Input id="workingHours" value={formData.workingHours} onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })} placeholder="Seg a sex, 8h às 18h" className={fieldInput} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website" className={fieldLabel}>Site</Label>
              <Input id="website" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://" className={fieldInput} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="notes" className={fieldLabel}>Observações</Label>
              <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30" />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={editingContact ? handleEditContact : handleAddContact} className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
              {editingContact ? "Salvar alterações" : "Adicionar contato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-2xl border-slate-200">
          <DialogHeader className="text-left">
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
              <Trash2 className="h-4 w-4 text-red-600" />
            </div>
            <DialogTitle className="text-lg font-semibold text-slate-900">Excluir contato?</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              "{contactToDelete?.name}" será removido da sua lista. Não dá para desfazer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setIsDeleteDialogOpen(false)} className="rounded-xl text-slate-600">
              Cancelar
            </Button>
            <Button onClick={handleDeleteContact} className="rounded-xl bg-red-600 text-white hover:bg-red-700">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
